import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { Event } from '../../types/Event';
import { apiClient } from '../../config/api';
import { getLocationDisplayName } from '../../utils/locationUtils';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import EventForm from './EventForm';
import EventVolunteers from './EventVolunteers';
import { eventTableConfig } from './EventTableConfig';

interface EventFormData {
  title: string;
  description: string;
  startDateTime: string;
  endDateTime: string;
  location: string;
  volunteersNeeded: boolean;
  maxVolunteers?: number;
  volunteerDescription?: string;
  privateEvent: boolean;
}

const EnhancedEventManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showVolunteers, setShowVolunteers] = useState(false);
  const [selectedEventForVolunteers, setSelectedEventForVolunteers] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load events and populate dynamic filter options
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await apiClient.get('/api/public/events');

        // Update location filter options dynamically
        const locations = Array.from(new Set(data.map((event: Event) => getLocationDisplayName(event.location))));
        eventTableConfig.filterOptions.location = locations.map(location => ({
          value: location as string,
          label: location as string
        }));
      } catch (error) {
        console.error('Failed to load events:', error);
      }
    };

    loadEvents();
  }, [refreshTrigger]);

  const actions = {
    handleCreate: () => {
      setEditingEvent(null);
      setShowForm(true);
    },

    handleEdit: (event: Event) => {
      setEditingEvent(event);
      setShowForm(true);
    },

    handleView: (event: Event) => {
      // For now, view is the same as edit
      // In the future, you could implement a read-only view
      setEditingEvent(event);
      setShowForm(true);
    },

    handleViewVolunteers: (event: Event) => {
      console.log('handleViewVolunteers called for event:', event.title);
      setSelectedEventForVolunteers(event);
      setShowVolunteers(true);
    }
  };

  const handleFormSubmit = async (formData: EventFormData) => {
    try {
      setIsLoading(true);

      if (editingEvent) {
        await apiClient.put(`/api/admin/events/${editingEvent.id}`, formData);
      } else {
        await apiClient.post('/api/admin/events', formData);
      }

      setShowForm(false);
      setEditingEvent(null);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh
    } catch (error) {
      console.error('Failed to save event:', error);
      alert('Failed to save event');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      await apiClient.delete(`/api/admin/events/${eventId}`);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Failed to delete event');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const handleVolunteersClose = () => {
    setShowVolunteers(false);
    setSelectedEventForVolunteers(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Manage Events"
          icon={<Calendar size={32} />}
          subtitle="Create, edit, and manage chorus events and performances"
        />

        <div className="flex justify-end">
          <button
            onClick={actions.handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <Calendar size={20} />
            Add New Event
          </button>
        </div>

        {/* Admin Table */}
        <AdminTable
          onView={actions.handleView}
          onEdit={actions.handleEdit}
          onDelete={handleDelete}
          onViewVolunteers={actions.handleViewVolunteers}
          config={eventTableConfig}
          refreshTrigger={refreshTrigger}
        />

        {/* Event Form Modal */}
        <EventForm
          item={editingEvent || undefined}
          isOpen={showForm}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
          isLoading={isLoading}
        />

        {/* Event Volunteers Modal */}
        {showVolunteers && selectedEventForVolunteers && (
          <EventVolunteers
            event={selectedEventForVolunteers}
            isOpen={showVolunteers}
            onClose={handleVolunteersClose}
          />
        )}
      </div>
    </div>
  );
};

export default EnhancedEventManagement;
